/* tslint:disable */
/* eslint-disable */
/**
 * AI Telemetry Hub — Read/Query API
 * Published integration contract for the AI Telemetry Hub read/query surface, generated code-first from AITH.Api and CI-diffed against the committed snapshot. This document is the stable integration contract for its stable operations; each operation\'s maturity and owning story are annotated on the operation itself as its first description line (`Owner: AITH-NN · Maturity: …`, or `Owner: GH-NN` where the owning record is a GitHub issue rather than a Jira story). Operations marked `Published (semantics owned by …)` are published here while their endpoint semantics remain owned by that record. Producers sending telemetry want the separate ingestion contract instead.  ## Authorization  Every operation requires a bearer token (Entra ID or Jonas IDP). The normative rule:  > Authenticated tenant scope is the source of authorization truth. A caller-supplied client code, where accepted, is only a candidate resource selector. It must be canonicalized and authorized against the resolved scope before data access. It must never replace the authenticated scope or broaden access.  Operations fall into four authorization categories:  - **A** — tenant derived from the token only; no client code accepted; all-tenant (staff) scope rejected: `GET /api/v1/client-usage`, `GET /api/v1/dashboard/home`, `GET /api/v1/dashboard/today`, `POST /api/v1/realtime/negotiate`. - **A-staff** — staff-only all-tenant aggregate; no client code accepted; single-tenant scope rejected: `GET /api/v1/dashboard/overview`, `GET /api/v1/dashboard/cost-allocation`, `GET /api/v1/dashboard/cost-by-model`, `GET /api/v1/dashboard/budget-alerts`, `GET /api/v1/dashboard/filter-options` (the applications, models and providers observed across every client, for the staff filter bar; no client appears in it), `GET /api/v1/clients` (the internal client directory — it enumerates registered clients rather than reading telemetry, so there is no tenant to select), `GET /api/v1/unregistered-clients` (the codes observed in telemetry that have no reference record — it **additionally requires the internal administrator role**, unlike every other operation in this category, because the set exists only to feed the configuration write and a non-administrator has no action to take on it). - **B** — the path `clientCode` is a candidate resource selector, canonicalized then authorized against the resolved token scope; a malformed or non-owned code yields the documented denial, never 400: `GET /api/v1/clients/{clientCode}/events`, `GET /api/v1/clients/{clientCode}/events/{eventId}` (cross-tenant and not-found indistinguishable: 404), `GET /api/v1/clients/{clientCode}/summary`, `PUT /api/v1/clients/{clientCode}/configuration` (additionally requires the internal administrator role), `POST /api/v1/clients/{clientCode}/exports`, `GET /api/v1/clients/{clientCode}/exports` (the client\'s own export jobs, newest-first and bounded — never a cross-client listing, because the operation has no form in which to ask for one), `GET /api/v1/clients/{clientCode}/exports/{jobId}` (cross-tenant and not-found indistinguishable: 404). - **C** — dual-mode staff drill-down; the `clientCode` query parameter is structurally optional, but staff callers MUST supply it and client-scoped callers MUST NOT: `GET /api/v1/dashboard/usage`.  ## Writes  This document contains three write operations, of different kinds. `PUT /api/v1/clients/{clientCode}/configuration` is a **resource** write: an administrator-only configuration change with optimistic-concurrency preconditions. `POST /api/v1/realtime/negotiate` is a **credential-minting** write: it writes no resource a caller can read back, but it issues a bounded-lifetime connection grant and records the server-side group membership that grant depends on, so it must never be cached and cannot be a GET. `POST /api/v1/clients/{clientCode}/exports` is this document\'s first **POST-create of a new server-identified resource**: it records an export job the server names, returns that name in `Location`, and answers 200 with the existing job when an identical request is already active, so a repeated submission creates nothing. Every other operation is a read.  ## Error responses  Authentication failures return a uniform 401 bearer challenge with no body guaranteed. Where an operation declares a 500 response it is declared status-only: no response body is guaranteed on a 500 (a deliberate documentation decision matching the runtime).
 *
 * The version of the OpenAPI document: 1.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
/**
 * The `200` body of `POST /api/v1/realtime/negotiate` (negotiate contract, "Response 200").
 * Published in the `query` OpenAPI document so `aith-ui` generates its client from the
 * contract rather than hand-modelling it.
 * @export
 * @interface RealtimeGrantResponse
 */
export interface RealtimeGrantResponse {
    /**
     * Client endpoint for the `tiles` hub.
     * @type {string}
     * @memberof RealtimeGrantResponse
     */
    url: string;
    /**
     * Service-signed connection token. Never logged, never persisted, never returned in an error body.
     * @type {string}
     * @memberof RealtimeGrantResponse
     */
    accessToken: string;
    /**
     * The grant's upper bound — at most the configured ceiling, and never beyond the presented token's own expiry.
     * @type {Date}
     * @memberof RealtimeGrantResponse
     */
    expiresAtUtc: Date;
    /**
     * The single group this grant permits.
     * @type {string}
     * @memberof RealtimeGrantResponse
     */
    clientCode: string;
    /**
     * The hub the grant admits the connection to.
     * @type {string}
     * @memberof RealtimeGrantResponse
     */
    hub: string;
    /**
     * Version of the tile-update message contract this stream carries.
     * @type {string}
     * @memberof RealtimeGrantResponse
     */
    schemaVersion: string;
}

/**
 * Check if a given object implements the RealtimeGrantResponse interface.
 */
export function instanceOfRealtimeGrantResponse(value: object): value is RealtimeGrantResponse {
    if (!('url' in value) || value['url'] === undefined) return false;
    if (!('accessToken' in value) || value['accessToken'] === undefined) return false;
    if (!('expiresAtUtc' in value) || value['expiresAtUtc'] === undefined) return false;
    if (!('clientCode' in value) || value['clientCode'] === undefined) return false;
    if (!('hub' in value) || value['hub'] === undefined) return false;
    if (!('schemaVersion' in value) || value['schemaVersion'] === undefined) return false;
    return true;
}

export function RealtimeGrantResponseFromJSON(json: any): RealtimeGrantResponse {
    return RealtimeGrantResponseFromJSONTyped(json, false);
}

export function RealtimeGrantResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): RealtimeGrantResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'url': json['url'],
        'accessToken': json['accessToken'],
        'expiresAtUtc': (new Date(json['expiresAtUtc'])),
        'clientCode': json['clientCode'],
        'hub': json['hub'],
        'schemaVersion': json['schemaVersion'],
    };
}

export function RealtimeGrantResponseToJSON(json: any): RealtimeGrantResponse {
    return RealtimeGrantResponseToJSONTyped(json, false);
}

export function RealtimeGrantResponseToJSONTyped(value?: RealtimeGrantResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'url': value['url'],
        'accessToken': value['accessToken'],
        'expiresAtUtc': value['expiresAtUtc'].toISOString(),
        'clientCode': value['clientCode'],
        'hub': value['hub'],
        'schemaVersion': value['schemaVersion'],
    };
}

